
import { Component, Vue, Watch, Prop } from "vue-property-decorator";
import { namespace } from "vuex-class";

import { Permission, PermissionExtended } from "@/store/user-management/types";

import vSelect from "vue-select";
import "@/scripts/dictionary";
import "@/scripts/array-operations";
import api from "@/services/api.service";

const UserManagement = namespace("userManagement");
const eventQueue = namespace("eventqueue");

@Component({
	components: {
		"v-select": vSelect
	}
})
export default class UserRolePermissions extends Vue {
	@Prop({ type: Array, default: [] })
	public value: any[];

	@UserManagement.Getter("getPermissions") public permissions: Permission[];

	@eventQueue.Getter public routingGroups: any[];
	@eventQueue.Action public loadRoutingGroups: () => void;

	public currentPermissions: any = {};
	private _rolePermissions: PermissionExtended[] = [];
	private InsightReports: string[] = [];
	private ExternalMenuLinks: string[] = [];

	private async mounted() {
		await this.getReportTypes();
		await this.getExternalMenuLinks();

		// Loop through our permissions to do the following computing;
		this.value.forEach((permission: any) => {
			// Convert our extended value if it requires to be a certain type
			if (permission.extended) {
				permission.extended = this.convertStringToType(permission.extended);
			}
		});

		this.$emit("component-loaded");
	}

	@Watch("value", { deep: true, immediate: true })
	private onValueChanged(newValue: PermissionExtended[]) {
		this._rolePermissions = [...newValue];
		this.currentPermissions = newValue ? newValue.toDictionary("permissionId") : {};
	}

	private addRolePermission(permission: PermissionExtended) {
		this._rolePermissions.push(permission);
	}

	private removeRolePermission(permission: PermissionExtended) {
		this._rolePermissions = this._rolePermissions.filter(rp => rp.permissionId !== permission.permissionId);
	}

	private updateOrAddExtendedRolePermission(permission: PermissionExtended) {
		// check if we already have a permission in the list
		let existing: PermissionExtended = this._rolePermissions.firstOrDefault(
			rp => rp.permissionId === permission.permissionId
		);

		if (existing) {
			// update it
			existing.extended = permission.extended;
		} else {
			// add it to the list
			this.addRolePermission(permission);
		}
	}

	/**
	 * Emit input and update the related arrays
	 */
	private raiseValueChanged() {
		this.$emit("input", this._rolePermissions);
		this.onValueChanged(this._rolePermissions);
	}

	/*
	 * Get report types and push titles onto ViewInsights permission
	 */
	private async getReportTypes() {
		try {
			const { data } = await api.GetReportTypesTitles();
			const result: string[] = data;
			if (result && result.length > 0) {
				this.InsightReports = result;
			}
		} catch (ex) {
			this.InsightReports = [];
		}
	}

	/*
	 *	Matches permissions against value model and groups by permissions category
	 */
	private get groupedPermissions(): any {
		if (!this.permissions) {
			return [];
		}

		const valueDictionary = this.currentPermissions;

		const rolePermissions: PermissionExtended[] = [];
		this.permissions.forEach(permission => {
			const permissionValue = valueDictionary[permission.permissionId.toString()];

			var tempPermission: PermissionExtended = {
				permissionId: permission.permissionId,
				title: permission.title,
				description: permission.description,
				hasPermission: permissionValue ? true : false,
				extendedValueDataType: permission.extendedValueDataType,
				extendedValueTitle: permission.extendedValueTitle,
				extended: permissionValue ? permissionValue.extended : null,
				category: permission.category,
				secondPermission: null
			};

			if (this.isPluginPermission(tempPermission)) {
				var pluginPerm = rolePermissions.filter(
					e =>
						e.title.split("/")[0] === tempPermission.title.split("/")[0] && e.title !== tempPermission.title
				);

				if (pluginPerm.length > 0) {
					pluginPerm[0].secondPermission = tempPermission;
				} else {
					rolePermissions.push(tempPermission);
				}
			} else {
				rolePermissions.push(tempPermission);
			}
		});

		// Group by category
		const categories = rolePermissions.reduce((group: any, permission: Permission) => {
			var section = permission.category ? permission.category : "General";
			(group[section] = group[section] || []).push(permission);
			return group;
		}, {});

		// Sort
		return Object.keys(categories)
			.map(categoryName => {
				return {
					title: categoryName,
					permissions: categories[categoryName]
				};
			})
			.sort((a, b) => (a.title > b.title ? 1 : a.title < b.title ? -1 : 0));
	}

	public permissionChangedCheckBox(permission: any, checked: boolean) {
		let permissionId = permission.permissionId;
		// if checked
		if (checked) {
			permission.hasPermission = true;
			// does this permission exist in our list?
			if (this._rolePermissions.some(p => p.permissionId === permissionId)) {
				// do nothing
				return;
			}
			this.addRolePermission(permission);
		} else {
			permission.hasPermission = false;
			// remove permission from list
			this.removeRolePermission(permission);
		}

		// notify anything that is listening that the array has been updated
		this.raiseValueChanged();
	}

	/*
	 *	Adds or removes permissions from model.
	 */
	public permissionChanged(permission: any) {
		let permissionId = { permission };

		if (permission.hasPermission) {
			if (
				this._rolePermissions.some(
					currentPermission => currentPermission.permissionId === permission.permissionId
				)
			) {
				return;
			}

			this.addRolePermission(permission);
		} else {
			this.removeRolePermission(permission);
		}

		this.raiseValueChanged();
	}

	/*
	 *	Update extended value for permissions
	 */
	private permissionExtendedValueChanged(permission: any, json: boolean = false) {
		this.updateOrAddExtendedRolePermission(permission);
		this.raiseValueChanged();
	}

	/*
	 * Checks if the permission is related to a plugin
	 */
	public isPluginPermission(permission) {
		return permission.title.includes("/CanBeViewed") || permission.title.includes("/CanBeInteractedWith");
	}

	/*
	 * Checks which plugin permission is given
	 */
	public isViewPluginPermission(permission) {
		return permission.title.includes("/CanBeViewed");
	}

	/*
	 * Returns the permission name in a readable format
	 */
	public getPluginName(permission) {
		var title = permission.title
			.split("/")[0]
			.replace(/([A-Z])/g, " $1")
			.trim();
		title = title.charAt(0).toUpperCase() + title.slice(1);
		return title + " Plugin";
	}

	/** If we need to convert our extended stringified value to a type */
	public convertStringToType(extended: any) {
		if (!extended) return extended;
		try {
			return JSON.parse(extended);
		} catch (er) {
			return extended;
		}
	}

	/*
	 * Method to add all report types from our ViewInsightReports
	 */
	private addAllReports() {

		// Evalulate if we actually got any reports to add
		if (!this.InsightReports || this.InsightReports.length == 0) return;

		// Used when a User role is being created
		if (this.value !== null && this.value.length == 0) {
			var updatedPermission: PermissionExtended = { permissionId: 95,
														  title: "ViewInsightReports",
														  extendedValueTitle: "Insight report types",
														  extendedValueDataType: "InsightsReports",
														  description: "View the specified reports",
														  category: "Insights",
														  hasPermission: true,
														  extended: this.InsightReports,
														  secondPermission: null																									
														};

			this.updateOrAddExtendedRolePermission(updatedPermission);						  
		} else {
			this.value.forEach(permission => {
				if (permission && permission.permission && permission.permission.title == "ViewInsightReports") {
					permission.extended = this.InsightReports;
				} else if (permission && permission.title == "ViewInsightReports") {
					permission.extended = this.InsightReports;
				}
			});
		}
	}

	/*
	 * Method to add all ExternalMenuLinks from our CanViewExternalMenuLinks
	 */
	private addAllExternalMenuLinks() {
		
		//  Evalulate if we actually got any menu links to add
		if (!this.ExternalMenuLinks || this.ExternalMenuLinks.length == 0) return;

		// Used when a User role is being created
		if (this.value !== null && this.value.length == 0) {
			var updatedPermission: PermissionExtended = { permissionId: 126,
														  title: "CanViewExternalMenuLinks",
														  extendedValueTitle: "External menu Links:",
														  extendedValueDataType: "ExternalMenuLinks",
														  description: "Allow a user view external menu links",
														  category: "Setup",
													      hasPermission: true,
													      extended: this.ExternalMenuLinks,
														  secondPermission: null																									
														};
			this.updateOrAddExtendedRolePermission(updatedPermission);						  
		}
		else { 
			this.value.forEach(permission => {
				if (permission && permission.permission && permission.permission.title == "CanViewExternalMenuLinks") {
					permission.extended = this.ExternalMenuLinks;
				} else if (permission && permission.title == "CanViewExternalMenuLinks") {
					permission.extended = this.ExternalMenuLinks;
				}
			});
		}
	}

	/*
	 * Method to remove all extended permissions for a given permission
	 */
	private removeAllExtendedPermssions(permissionTitle: string) {

		// Used when a User role is being created
		if (this.value !== null && this.value.length == 0) {
			var updatedPermission: PermissionExtended = null;

			if (permissionTitle == "ViewInsightReports") { 
				updatedPermission = { permissionId: 95,
									  title: "ViewInsightReports",
									  extendedValueTitle: "Insight report types",
									  extendedValueDataType: "InsightsReports",
									  description: "View the specified reports",
									  category: "Insights",
									  hasPermission: true,
									  extended: null,
									  secondPermission: null																									
									};
			} else if (permissionTitle == "CanViewExternalMenuLinks") { 
				updatedPermission = { permissionId: 126,
									  title: "CanViewExternalMenuLinks",
									  extendedValueTitle: "External menu Links:",
									  extendedValueDataType: "ExternalMenuLinks",
									  description: "'Allow a user view external menu links",
									  category: "Setup",
									  hasPermission: true,
									  extended: null,
									  secondPermission: null																									
									};	
			}

			if (updatedPermission !== null) {
				this.updateOrAddExtendedRolePermission(updatedPermission);	
			}					  
		} else { 
			if (!permissionTitle) return;
				this.value.forEach(permission => {
					if (permission && permission.permission && permission.permission.title == permissionTitle) {
						permission.extended = [];
					} else if (permission.title == permissionTitle) {
						permission.extended = [];
					}
			});
		}
	}

	/*
	 * Get ExternalMenuLinks and push titles onto ExternalMenuLinks permission
	 */
	private async getExternalMenuLinks() {
		try {
			const { data } = await api.GetExternalMenuLinkTitles();
			const result: string[] = data;
			if (result && result.length > 0) {
				this.ExternalMenuLinks = result;
			}
		} catch (ex) {
			this.ExternalMenuLinks = [];
		}
	}
}
